import { useRef, useState } from "react";
import { Camera, Loader2, X, FileText } from "lucide-react";
import { uploadFile, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatusBadge({ variant, children, testid }) {
  const styles = {
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
    due: "bg-amber-50 text-amber-700 border-amber-200",
    warning: "bg-red-50 text-red-700 border-red-200",
    neutral: "bg-slate-100 text-slate-600 border-slate-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${styles[variant] || styles.neutral}`}
    >
      {children}
    </span>
  );
}

export function PhotoUpload({ fileId, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const res = await uploadFile(f);
      onChange(res.file_id);
      toast.success("Foto hochgeladen");
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={testid} />
      {fileId ? (
        <div className="relative">
          <img src={fileUrl(fileId)} alt="Nachweis" className="h-14 w-14 rounded-lg object-cover border border-slate-200" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -right-2 -top-2 rounded-full bg-red-500 p-0.5 text-white"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:border-teal-400 hover:text-teal-600 transition-colors"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          Foto
        </button>
      )}
    </div>
  );
}

export function FileLink({ fileId, label }) {
  if (!fileId) return <span className="text-slate-400 text-sm">—</span>;
  return (
    <a
      href={fileUrl(fileId)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
    >
      <FileText className="h-4 w-4" /> {label || "Ansehen"}
    </a>
  );
}

export const INTERVAL_LABELS = {
  daily: "täglich",
  operating_day: "Betriebstag",
  weekly: "wöchentlich",
  monthly: "monatlich",
  spontan: "spontan",
};

export const TYPE_LABELS = {
  cleaning: "Reinigung",
  temperature: "Temperatur",
  generic: "Prüfung",
};
